// ============================================================
// Liga Ajupam — CTA "Inscribite en la nueva edición".
// Se muestra en el INICIO de una edición ARCHIVADA (modo solo-lectura) cuando
// existe una edición POSTERIOR de la MISMA FAMILIA con inscripción abierta.
// Objetivo: un jugador que entra a ver su edición vieja (típico: abrió el link
// de un compañero o la app instalada) encuentra un camino llamativo para
// anotarse en la edición nueva, en vez de quedar sin saber qué hacer.
//
// Datos: get_player_entry_options(dni) ya devuelve open_registrations (ligas con
// inscripción abierta) + family_name por liga. Matcheamos la familia de la
// edición archivada que se está viendo. Fallback: si no hay match por familia
// (datos sin family_name), ofrecemos cualquier inscripción abierta.
// Colores institucionales: primary (#035aa6) + secondary/accent (lima).
// ============================================================
import React, { useEffect, useState } from 'react';
import { Megaphone, ArrowRight } from 'lucide-react';
import { getPlayerEntryOptions } from '../services/api/auth';
import type { OpenRegistrationOption } from '../types';

interface NewEditionCtaProps {
  /** DNI del jugador logueado (para resolver sus opciones de ingreso). */
  dni: number;
  /** league_id de la edición archivada que se está viendo (para matchear familia). */
  currentLeagueId?: string | null;
  /** Dispara el flujo de inscripción en la edición abierta elegida. */
  onEnroll: (open: OpenRegistrationOption) => void;
}

export const NewEditionCta: React.FC<NewEditionCtaProps> = ({ dni, currentLeagueId, onEnroll }) => {
  const [open, setOpen] = useState<OpenRegistrationOption | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const opts = await getPlayerEntryOptions(dni).catch(() => null);
      if (cancelled || !opts || opts.open_registrations.length === 0) return;

      // Familia de la edición archivada actual (si la resolvemos).
      const currentFamily = currentLeagueId
        ? opts.leagues.find((l) => l.league_id === currentLeagueId)?.family_name ?? null
        : null;

      // Preferimos una edición abierta de la MISMA familia; si no hay (o no hay
      // datos de familia), caemos a la primera inscripción abierta disponible.
      const sameFamily = currentFamily
        ? opts.open_registrations.find((o) => o.family_name && o.family_name === currentFamily)
        : undefined;
      const chosen = sameFamily ?? opts.open_registrations[0] ?? null;
      if (chosen) setOpen(chosen);
    })();
    return () => { cancelled = true; };
  }, [dni, currentLeagueId]);

  if (!open) return null;

  const edicion = open.edition_number ? `${open.edition_number}ª edición` : 'Nueva edición';

  return (
    <button
      type="button"
      onClick={() => onEnroll(open)}
      className="group relative mx-4 my-3 flex w-[calc(100%-2rem)] items-center gap-4 overflow-hidden rounded-2xl bg-primary px-4 py-4 text-left shadow-lg ring-1 ring-secondary/40 transition-transform active:scale-[0.99] hover:shadow-xl"
    >
      {/* Acento lima institucional a la izquierda */}
      <span className="absolute inset-y-0 left-0 w-1.5 bg-secondary" aria-hidden />

      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary shadow-md">
        <Megaphone size={22} strokeWidth={2.25} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-black uppercase tracking-wider text-secondary">
          ¡Inscripción abierta!
        </span>
        <span className="block truncate text-base font-extrabold text-white leading-tight">
          {open.name}
        </span>
        <span className="block text-xs font-medium text-white/80">
          Anotate en la {edicion} reusando tus datos.
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-1 rounded-full bg-secondary px-3 py-2 text-xs font-black uppercase tracking-wide text-primary transition-transform group-hover:translate-x-0.5">
        Inscribirme
        <ArrowRight size={16} strokeWidth={2.5} />
      </span>
    </button>
  );
};

export default NewEditionCta;
